import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { AnimatePresence, MotionConfig, motion, type HTMLMotionProps } from "framer-motion";
import { AlertCircle, Check, Info, X } from "lucide-react";
import { cn } from "@/lib/utils";

/* ═══════════════════════════════════════════════════════════════════════
 *  Toast System
 *  Provides ToastProvider, Toast, ToastTitle, ToastDescription,
 *  ToastClose, ToastViewport, and useToast hook.
 * ═══════════════════════════════════════════════════════════════════════ */

/* ── Types ───────────────────────────────────────────────────────────── */

type ToastVariant = "default" | "destructive" | "success";

interface ToastData {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  duration?: number;
}

interface ToastState {
  toasts: ToastData[];
}

type ToastAction =
  | { type: "ADD"; toast: ToastData }
  | { type: "DISMISS"; id: string }
  | { type: "REMOVE"; id: string };

/* ── Reducer ─────────────────────────────────────────────────────────── */

function toastReducer(state: ToastState, action: ToastAction): ToastState {
  switch (action.type) {
    case "ADD":
      return { toasts: [...state.toasts, action.toast] };
    case "DISMISS":
    case "REMOVE":
      return { toasts: state.toasts.filter((t) => t.id !== action.id) };
    default:
      return state;
  }
}

/* ── Context ─────────────────────────────────────────────────────────── */

interface ToastContextValue {
  toasts: ToastData[];
  toast: (data: Omit<ToastData, "id">) => string;
  dismiss: (id: string) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

let toastCount = 0;

/* ── ToastProvider ───────────────────────────────────────────────────── */

export interface ToastProviderProps {
  children: React.ReactNode;
  /** Default auto-dismiss duration in ms. Default 5000. */
  duration?: number;
}

/** Wrap your app in ToastProvider to enable toast notifications. */
function ToastProvider({ children, duration = 5000 }: ToastProviderProps) {
  const [state, dispatch] = React.useReducer(toastReducer, { toasts: [] });

  const dismiss = React.useCallback((id: string) => {
    dispatch({ type: "DISMISS", id });
  }, []);

  const toast = React.useCallback(
    (data: Omit<ToastData, "id">) => {
      const id = `toast-${++toastCount}`;
      dispatch({ type: "ADD", toast: { ...data, id } });

      const timeout = data.duration ?? duration;
      if (timeout > 0) {
        setTimeout(() => dismiss(id), timeout);
      }

      return id;
    },
    [duration, dismiss]
  );

  return (
    <ToastContext.Provider value={{ toasts: state.toasts, toast, dismiss }}>
      {children}
      <MotionConfig reducedMotion="user">
        <ToastViewport>
          <AnimatePresence initial={false}>
            {state.toasts.map((t) => (
              <Toast
                key={t.id}
                variant={t.variant}
                title={t.title}
                description={t.description}
                onDismiss={() => dismiss(t.id)}
              />
            ))}
          </AnimatePresence>
        </ToastViewport>
      </MotionConfig>
    </ToastContext.Provider>
  );
}

/* ── useToast Hook ───────────────────────────────────────────────────── */

function useToast() {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  return ctx;
}

/* ── Toast Variants ──────────────────────────────────────────────────── */

const toastVariants = cva(
  "group pointer-events-auto relative flex items-start gap-3 self-end overflow-hidden border border-border bg-card py-3 pl-3 pr-10 text-card-foreground shadow-[0_10px_30px_-18px_rgba(0,58,67,0.45)]",
  {
    variants: {
      variant: {
        default: "",
        destructive: "border-destructive/30",
        success: "border-success/30",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const toastIconVariants = cva(
  "mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
  {
    variants: {
      variant: {
        default: "bg-primary/10 text-primary",
        destructive: "bg-destructive/12 text-destructive",
        success: "bg-success/15 text-success",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const TOAST_ICONS = { default: Info, destructive: AlertCircle, success: Check } as const;

/** Springy pop-in, quick fade-out. MotionConfig turns this off for reduced motion. */
const TOAST_SPRING = { type: "spring", stiffness: 520, damping: 34, mass: 0.9 } as const;

/* ── Toast ────────────────────────────────────────────────────────────── */

/** How long a toast stays a pill before opening to show its description. */
const TOAST_EXPAND_DELAY_MS = 550;

export interface ToastProps
  extends Omit<HTMLMotionProps<"li">, "children" | "title">,
    VariantProps<typeof toastVariants> {
  title?: React.ReactNode;
  description?: React.ReactNode;
  onDismiss?: () => void;
}

/**
 * Opens as a pill (icon + title), then widens into a card to show the
 * description. The description is always in the DOM so screen readers
 * announce the full message straight away.
 */
const Toast = React.forwardRef<HTMLLIElement, ToastProps>(
  ({ className, variant, title, description, onDismiss, ...props }, ref) => {
    const Icon = TOAST_ICONS[variant ?? "default"];
    const hasDescription = Boolean(description);
    const [expanded, setExpanded] = React.useState(false);

    React.useEffect(() => {
      if (!hasDescription) return;
      const timer = window.setTimeout(() => setExpanded(true), TOAST_EXPAND_DELAY_MS);
      return () => window.clearTimeout(timer);
    }, [hasDescription]);

    const open = hasDescription && expanded;

    return (
      <motion.li
        ref={ref}
        layout
        role={variant === "destructive" ? "alert" : "status"}
        initial={{ opacity: 0, y: 16, scale: 0.85 }}
        animate={{ opacity: 1, y: 0, scale: 1, transition: TOAST_SPRING }}
        exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.15 } }}
        transition={{ layout: TOAST_SPRING }}
        style={{ borderRadius: open ? 22 : 999 }}
        onHoverStart={() => hasDescription && setExpanded(true)}
        onFocus={() => hasDescription && setExpanded(true)}
        className={cn(toastVariants({ variant }), open ? "w-full" : "w-auto max-w-full", className)}
        {...props}
      >
        <motion.span
          layout="position"
          className={toastIconVariants({ variant })}
          initial={{ scale: 0.4, rotate: -30 }}
          animate={{ scale: 1, rotate: 0, transition: { ...TOAST_SPRING, delay: 0.05 } }}
          aria-hidden="true"
        >
          <Icon className="h-4 w-4" strokeWidth={2.5} />
        </motion.span>
        <motion.div layout="position" className="grid min-w-0 pt-1">
          {title && <ToastTitle>{title}</ToastTitle>}
          {hasDescription && (
            <motion.div
              initial={false}
              animate={open ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
              transition={open ? { height: TOAST_SPRING, opacity: { duration: 0.2, delay: 0.08 } } : { duration: 0 }}
              className="overflow-hidden"
            >
              <ToastDescription className="pt-0.5">{description}</ToastDescription>
            </motion.div>
          )}
        </motion.div>
        {onDismiss && <ToastClose onClick={onDismiss} />}
      </motion.li>
    );
  }
);
Toast.displayName = "Toast";

/* ── ToastTitle ───────────────────────────────────────────────────────── */

const ToastTitle = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("whitespace-normal text-sm font-semibold leading-snug", className)}
      {...props}
    />
  )
);
ToastTitle.displayName = "ToastTitle";

/* ── ToastDescription ────────────────────────────────────────────────── */

const ToastDescription = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn("text-sm leading-snug text-muted-foreground", className)}
      {...props}
    />
  )
);
ToastDescription.displayName = "ToastDescription";

/* ── ToastClose ──────────────────────────────────────────────────────── */

const ToastClose = React.forwardRef<HTMLButtonElement, React.ButtonHTMLAttributes<HTMLButtonElement>>(
  ({ className, ...props }, ref) => (
    <button
      ref={ref}
      type="button"
      className={cn(
        "absolute right-2.5 top-2.5 rounded-full p-1 text-muted-foreground opacity-60 transition-opacity",
        "hover:bg-muted hover:opacity-100 group-hover:opacity-100",
        "focus:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring",
        className
      )}
      aria-label="Dismiss notification"
      {...props}
    >
      <X className="h-4 w-4" />
    </button>
  )
);
ToastClose.displayName = "ToastClose";

/* ── ToastViewport ───────────────────────────────────────────────────── */

const ToastViewport = React.forwardRef<HTMLOListElement, React.HTMLAttributes<HTMLOListElement>>(
  ({ className, ...props }, ref) => (
    <ol
      ref={ref}
      aria-label="Notifications"
      aria-live="polite"
      className={cn(
        "pointer-events-none fixed top-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:top-auto sm:bottom-0 sm:right-0 sm:flex-col md:max-w-[420px]",
        className
      )}
      {...props}
    />
  )
);
ToastViewport.displayName = "ToastViewport";

export {
  ToastProvider,
  useToast,
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastViewport,
  toastVariants,
  toastIconVariants,
};
